import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ImageWithFallback } from "./ImageWithFallback";
import { themes } from "../../utils/themes";

function RevelationSection({
  data,
}: {
  data: (typeof themes)[0];
  index: number;
}) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [_isInView, setIsInView] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Word fades in first from the bottom, then fades out slowly
  const wordOpacity = useTransform(scrollYProgress, [0, 0.2, 1], [0, 1, 0]);
  const wordY = useTransform(
    scrollYProgress,
    [0, 0.2, 1],
    ["30%", "0%", "-10%"]
  );

  // Background image fades in behind the text
  const imageOpacity = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const imageY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  // Verse and reflection fade in later with smoother transition
  const contentOpacity = useTransform(scrollYProgress, [1, 1], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.1, 1], ["10%", "0%"]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={sectionRef}
      className="relative h-[120vh] w-screen overflow-hidden bg-white"
      style={{ scrollSnapAlign: "start" }}
    >
      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0"
        style={{ opacity: imageOpacity, y: imageY }}
      >
        <ImageWithFallback
          src={data.image}
          alt={data.title}
          className="w-full h-full object-cover grayscale"
        />
        <motion.div
          className="absolute inset-0 bg-white"
          style={{
            opacity: useTransform(scrollYProgress, [0, 0.3, 0.6], [1, 0.7, 0]),
          }}
        />
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      {/* Large Word - Center */}
      <motion.div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: wordOpacity, y: wordY }}
      >
        <h2
          style={{
            fontFamily: "League Spartan, sans-serif",
            fontSize: "clamp(4rem, 12vw, 14rem)",
            fontWeight: "900",
            color: "#000000",
            letterSpacing: "-0.04em",
            lineHeight: "0.85",
            textTransform: "uppercase",
            textAlign: "center",
          }}
        >
          {data.title}
        </h2>
      </motion.div>

      {/* Text Content Overlay */}
      <motion.div
        className="absolute inset-0 flex items-end justify-start p-4 sm:p-8 lg:p-16 xl:p-24 pointer-events-none"
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <div className="max-w-3xl mb-12 lg:mb-16">
          {/* Reflection */}
          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: "clamp(1.125rem, 2vw, 1.5rem)",
              color: "#FFFFFF",
              lineHeight: "1.6",
              marginBottom: "2rem",
              fontStyle: "italic",
              textShadow: "0 2px 20px rgba(0,0,0,0.5)",
            }}
          >
            {data.reflection}
          </p>

          {/* Bible Verse */}
          <div className="space-y-3">
            <blockquote
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: "clamp(1rem, 1.5vw, 1.25rem)",
                color: "#FFFFFF",
                lineHeight: "1.7",
                fontStyle: "italic",
                textShadow: "0 2px 20px rgba(0,0,0,0.5)",
              }}
            >
              "{data.verse}"
            </blockquote>
            <div className="flex items-center gap-4">
              <div className="w-12 h-px bg-white/80" />
              <cite
                style={{
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  color: "#FFFFFF",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  fontStyle: "normal",
                  textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                }}
              >
                {data.reference}
              </cite>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export function TheWordRevealed() {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <section
      ref={containerRef}
      className="relative bg-neutral-50"
      style={{ scrollSnapType: "y proximity" }}
    >
      {/* Scroll Sections */}
      {themes.map((theme, index) => (
        <RevelationSection key={theme.title} data={theme} index={index} />
      ))}
    </section>
  );
}
